import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { AccountingAccountDA } from '../../services/admin/accounting.account.service';
import { GlobalSwal } from '../../styles/swal.style';
import { Error } from '../../models/error.model';
import { Company } from '../../models/company.model';
import { AccountingAccount } from '../../models/accounting.account.model';
import { getError } from '../../languages/translations/response';
import { legendIconInvalidRequired, legendIconInvalid, legendIconValidInvalidRestart, legendIconValidRequired } from '../../utils/legend.data.util';
import { modalHide } from '../../utils/modal.util';
import { evaluateLegendRequiredIcon } from '../../scripts/validate.legend.script';
import { isValidObjectAttributes } from '../../scripts/compare.object.script';
import { expressions } from '../../libraries/regular.expressions.library';
import { languageValue } from '../../libraries/language.library';
import { treasuryAccountTypeValue } from '../../libraries/treasury.account.type.library';
import { uploadTooltip } from '../../utils/tooltip.util';
import { suffixLegend } from '../../scripts/name.legend.script';
import { errorWebHandler } from '../../scripts/error.web.handler.script';
import { formatAccountingAccount } from '../../scripts/format.value.script';

import AppLegend from '../element/Legend';
import AppInputCurrency from '../element/InputCurrency';

export interface AppModalAccountingAccountCreateProps {
  companyForUser: Company | null,
  type: string,
  accountingAccounts: AccountingAccount[],
  setAccountingAccount: Dispatch<SetStateAction<{value: string, valid: boolean, object: any}>>,
  setAccountingAccounts: Dispatch<SetStateAction<AccountingAccount[] | null>>,
  loadAccountingAccountsForCompany: Function
};

let errorResponse: Error, accountingAccountResponse: AccountingAccount;

const AppModalAccountingAccountCreate: React.FunctionComponent<AppModalAccountingAccountCreateProps> = ({companyForUser, accountingAccounts, type, setAccountingAccount, setAccountingAccounts, loadAccountingAccountsForCompany}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [mode, setMode] = useState<'sibling' | 'sub'>('sibling')
  const [referenceAccountId, setReferenceAccountId] = useState('')
  const [code, setCode] = useState({value: '', valid: false})
  const [currency, setCurrency] = useState({value: '', valid: false, object: null as any})
  const [name, setName] = useState({value: {en: '', fr: '', it: '', de: ''}, valid: {en: false, fr: false, it: false, de: false}})
  const [loadingCode, setLoadingCode] = useState(false)

  const groupParentId = accountingAccounts[0]?.id_parent ?? ''

  const effectiveParentId = mode === 'sibling' ? groupParentId : referenceAccountId

  // Fetch next code when mode or reference changes
  useEffect(() => {
    if (!companyForUser || !effectiveParentId) {
      setCode(prev => ({...prev, value: ''}))
      return
    }

    setLoadingCode(true)
    AccountingAccountDA.getNextCode(companyForUser.id, effectiveParentId).then((response) => {
      if (response.status === 200) {
        const nextCode = response.data?.next_code ?? ''
        setCode({value: nextCode, valid: nextCode.length > 0})
      }
    }).catch(() => {
      // preview failed — user can still type manually
    }).finally(() => {
      setLoadingCode(false)
    })

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, effectiveParentId, mode])

  // Auto-select first reference account for sub mode
  useEffect(() => {
    if (mode === 'sub' && !referenceAccountId && accountingAccounts.length > 0) {
      setReferenceAccountId(accountingAccounts[0].id)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, accountingAccounts])

  const handleChangeMode = (newMode: 'sibling' | 'sub') => {
    if (newMode === mode) return
    setMode(newMode)
    setCode({value: '', valid: false})
    if (newMode === 'sibling') {
      setReferenceAccountId('')
    }
  }

  const handleChangeReferenceAccount = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setReferenceAccountId(event.target.value)
    setCode({value: '', valid: false})
  }

  const submitAccountingAccountCreate = async () => {
    setLoadIndicator('on')

    let nameIsValid: boolean = isValidObjectAttributes(name.valid)

    if (companyForUser && code.valid && currency.valid && nameIsValid && effectiveParentId) {
      const idSibling = mode === 'sibling' && accountingAccounts.length > 0 ? accountingAccounts[0].id : undefined

      await AccountingAccountDA.postAccountingAccount(companyForUser.id, effectiveParentId, 'leave', code.value, name.value, currency.value, idSibling).then( (response) => {
        if (response.status === 201) {
          accountingAccountResponse = {...response.data, available: true}

          setAccountingAccount({value: accountingAccountResponse.id, object: accountingAccountResponse, valid: true})
          setAccountingAccounts([accountingAccountResponse, ...accountingAccounts])
          loadAccountingAccountsForCompany(companyForUser.id)

          GlobalSwal.fire({
            title: lang.labels.successfullyCreatedAccountingAccount,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            executeHideModalAccountingAccountCreate()
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        if (!code.value.length) legendIconInvalid('modal-accounting-account-create-input-code', 'modal-accounting-account-create-legend-code-required')
        for (let index = 0; index < Object.keys(name.value).length; index++) {
          if (!name.value[Object.keys(name.value)[index] as keyof typeof name.valid].length) legendIconInvalid(`modal-accounting-account-create-input-name${suffixLegend(index)}`, `modal-accounting-account-create-legend-name-required${suffixLegend(index)}`)
        }
        setLoadIndicator('off')
      })
    }
  }

  const handleChangeCode = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setCode({...code, value: event.target.value})
  }

  const handleChangeName = (item: string, event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setName(name => ({...name, value: {...name.value, [item]: event.target.value}}))
  }

  const validateCode = () => {
    evaluateLegendRequiredIcon(/^.{1,}$/, code, setCode, 'modal-accounting-account-create-input-code', 'modal-accounting-account-create-legend-code-valid', 'modal-accounting-account-create-legend-code-required')
  }

  const validateName = (index: number, item: string) => {
    if (expressions && expressions.name.test(name.value[item as keyof typeof name.value])) {
      setName(name => ({...name, valid: {...name.valid, [item]: true}}))
      legendIconValidRequired(`modal-accounting-account-create-input-name${suffixLegend(index)}`, `modal-accounting-account-create-legend-name-valid${suffixLegend(index)}`, `modal-accounting-account-create-legend-name-required${suffixLegend(index)}`)
    } else {
      setName(name => ({...name, valid: {...name.valid, [item]: false}}))
      legendIconInvalidRequired(name.value[item as keyof typeof name.value], `modal-accounting-account-create-input-name${suffixLegend(index)}`, `modal-accounting-account-create-legend-name-valid${suffixLegend(index)}`, `modal-accounting-account-create-legend-name-required${suffixLegend(index)}`)
    }
  }

  const executeHideModalAccountingAccountCreate = () => {
    modalHide('modal-accounting-account-create')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendIconValidInvalidRestart('modal-accounting-account-create-input-code', 'modal-accounting-account-create-legend-code-valid')
    legendIconValidInvalidRestart('modal-accounting-account-create-input-code', 'modal-accounting-account-create-legend-code-required')

    for (var index in Object.keys(name.valid)) {
      legendIconValidInvalidRestart(`modal-accounting-account-create-input-name${suffixLegend(index)}`, `modal-accounting-account-create-legend-name-valid${suffixLegend(index)}`)
      legendIconValidInvalidRestart(`modal-accounting-account-create-input-name${suffixLegend(index)}`, `modal-accounting-account-create-legend-name-required${suffixLegend(index)}`)
    }

    setMode('sibling')
    setReferenceAccountId('')
    setCode({value: '', valid: false})
    setName({value: {en: '', fr: '', it: '', de: ''}, valid: {en: false, fr: false, it: false, de: false}})

    if (companyForUser?.currency) {
      setCurrency({value: companyForUser.currency?.code, valid: true, object: companyForUser.currency})
    } else {
      setCurrency({value: '', valid: false, object: null})
    }
  }

  useEffect( () => {
    if (companyForUser?.currency && !currency.value) {
      setCurrency({value: companyForUser.currency?.code, valid: true, object: companyForUser.currency})
    }
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser])

  return (
    <div id="modal-accounting-account-create" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.addAccountingAccount}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <table className="table align-middle gy-0 mb-5">
                <tbody className="fw-bold">
                  <tr>
                    <td className="w-200px text-muted">{lang.labels.typeOfTreasuryAccount}:</td>
                    <td className="min-w-200px">{treasuryAccountTypeValue(lang, type)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="row mb-4">
                <label className="col-lg-2 col-form-label">{lang.labels.creationMode}</label>
                <div className="col-lg-10">
                  <div className="btn-group w-100" role="group">
                    <button type="button" className={`btn btn-sm ${mode === 'sibling' ? 'btn-primary' : 'btn-light-primary'}`} onClick={() => handleChangeMode('sibling')}>
                      {lang.labels.siblingAccount}
                    </button>
                    <button type="button" className={`btn btn-sm ${mode === 'sub' ? 'btn-primary' : 'btn-light-primary'}`} onClick={() => handleChangeMode('sub')}>
                      {lang.labels.subAccount}
                    </button>
                  </div>
                </div>
              </div>
              { mode === 'sub' && accountingAccounts.length > 0 &&
                <div className="row mb-4">
                  <label className="col-lg-2 col-form-label">{lang.labels.referenceAccount}</label>
                  <div className="col-lg-10">
                    <select className="form-select" value={referenceAccountId} onChange={handleChangeReferenceAccount}>
                      { accountingAccounts.map((account) => (
                        <option key={account.id} value={account.id}>
                          {formatAccountingAccount(account, companyForUser?.language ?? 'fr')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              }
              <div className="row mb-2">
                <label className="col-lg-2 col-form-label required">{lang.labels.code}</label>
                <div className="col-lg-10">
                  <input id="modal-accounting-account-create-input-code" className="form-control" type="text" autoComplete="off" name="code" value={code.value} onChange={handleChangeCode} onKeyUp={validateCode} onBlur={validateCode} placeholder={loadingCode ? '...' : ''} />
                  <AppLegend component={"modal-accounting-account-create"} attribute={{check: code.valid, name: "code", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  <div className="form-text text-muted">{mode === 'sibling' ? lang.labels.siblingCodeHint : lang.labels.childCodeHint}</div>
                </div>
              </div>
              <div className="row mb-2">
                <label className="col-lg-2 col-form-label required">{lang.labels.currency}</label>
                <div className="col-lg-10">
                  <AppInputCurrency component={"modal-accounting-account-create"} currency={currency} setCurrency={setCurrency}></AppInputCurrency>
                </div>
              </div>
              <div className="row">
                <label className="col-form-label d-flex align-items-center">
                  <span className="required">{lang.labels.name}</span>
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.enterNameOfRequestedLanguages}><FaExclamationCircle /></i>
                </label>
                { Object.entries(name.value).map (( (item, index) => { return (
                  <div key={index} className="my-1">
                    <input id={`modal-accounting-account-create-input-name${suffixLegend(index)}`} className="form-control" type="text" autoComplete="off" name="name" placeholder={languageValue(lang, item[0].toUpperCase())} value={item[1]} onChange={(event) => handleChangeName(item[0], event)} onKeyUp={() => validateName(index, item[0])} onBlur={() => validateName(index, item[0])} />
                    <AppLegend component={"modal-accounting-account-create"} attribute={{check: name.valid[item[0] as keyof typeof name.valid], name: "name", index: index, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
                  </div>
                )}))}
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalAccountingAccountCreate}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitAccountingAccountCreate}>
              <span className="indicator-label">{lang.labels.create}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
};

export default AppModalAccountingAccountCreate;
